/**
 * Timeline view of a tracker board (monday.com-style).
 *
 * Generic over the tracker's item source: rows are TimelineItemData
 * (briefs pages map briefs, custom trackers map tracker items). Plots items
 * along a horizontal date axis, one swimlane per `lane` (project / status /
 * single lane). Items without a date are omitted. Read-only — dates are
 * edited in Table view.
 *
 * Prefers a `date_range` ("Timeline") column and plots each item as a span
 * BAR from start to end; if the board has no range column it falls back to the
 * first `date` column and plots single-date PILLS.
 */
import { useMemo } from 'react';
import { format } from 'date-fns';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';

import { EmptyState } from '@/shared/ui';
import { parseLocalDate } from '../../lib/parseLocalDate';
import { useCanEditTracker } from '../../lib/tracker-access';
import type { CustomColumn } from '../../lib/useTrackersBoard';
import type { DateRangeValue } from '../CustomCells';
import { useTranslation } from 'react-i18next';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Source-agnostic timeline item. */
export interface TimelineItemData {
  id: string;
  title: string;
  laneId: string;
  laneTitle: string;
  laneColor: string;
  /** The item's custom column values, keyed by column id. */
  values: Record<string, unknown> | undefined;
}

interface TimelineViewProps {
  items: TimelineItemData[];
  customColumns: CustomColumn[];
  onItemClick?: (itemId: string) => void;
}

interface DatedItem {
  item: TimelineItemData;
  start: Date;
  end: Date;
}

interface Lane {
  id: string;
  title: string;
  color: string;
  dated: DatedItem[];
}

function parseDate(value: unknown): Date | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  return parseLocalDate(value) ?? null;
}

/** A single date → a zero-width span; a range → its start/end (end ≥ start). */
function parseSpan(value: unknown, isRange: boolean): { start: Date; end: Date } | null {
  if (isRange) {
    if (!value || typeof value !== 'object') return null;
    const { start, end } = value as DateRangeValue;
    const from = parseDate(start);
    if (!from) return null;
    const to = parseDate(end) ?? from;
    return { start: from, end: to.getTime() < from.getTime() ? from : to };
  }
  const date = parseDate(value);
  return date ? { start: date, end: date } : null;
}

export function TimelineView({ items, customColumns, onItemClick }: TimelineViewProps) {
  const { t } = useTranslation('trackers');
  const canEdit = useCanEditTracker();

  // Prefer a Date Range column (bars); fall back to the first Date column (pills).
  const rangeColumn = useMemo(
    () => customColumns.find((c) => c.type === 'date_range'),
    [customColumns],
  );
  const dateColumn = useMemo(() => customColumns.find((c) => c.type === 'date'), [customColumns]);
  const timelineColumn = rangeColumn ?? dateColumn;
  const isRange = Boolean(rangeColumn);

  const lanes = useMemo<Lane[]>(() => {
    if (!timelineColumn) return [];
    const byLane = new Map<string, Lane>();
    for (const item of items) {
      const span = parseSpan(item.values?.[timelineColumn.id], isRange);
      if (!span) continue;
      let lane = byLane.get(item.laneId);
      if (!lane) {
        lane = { id: item.laneId, title: item.laneTitle, color: item.laneColor, dated: [] };
        byLane.set(item.laneId, lane);
      }
      lane.dated.push({ item, start: span.start, end: span.end });
    }
    const result = [...byLane.values()];
    for (const lane of result) {
      lane.dated.sort((a, b) => a.start.getTime() - b.start.getTime());
    }
    return result;
  }, [items, timelineColumn, isRange]);

  // Axis range: min(start)..max(end) across all items, padded so edge marks
  // don't clip. Falls back to a small window when only one date exists.
  const range = useMemo(() => {
    const starts = lanes.flatMap((l) => l.dated.map((d) => d.start.getTime()));
    const ends = lanes.flatMap((l) => l.dated.map((d) => d.end.getTime()));
    if (starts.length === 0) return null;
    let min = Math.min(...starts);
    let max = Math.max(...ends);
    const span = max - min;
    const pad = Math.max(span * 0.06, 4 * DAY_MS);
    min -= pad;
    max += pad;
    return { min, max };
  }, [lanes]);

  const monthTicks = useMemo(() => {
    if (!range) return [];
    const ticks: Array<{ label: string; leftPct: number }> = [];
    const cursor = new Date(range.min);
    cursor.setDate(1);
    cursor.setHours(0, 0, 0, 0);
    // Start from the first month boundary inside the range.
    if (cursor.getTime() < range.min) cursor.setMonth(cursor.getMonth() + 1);
    while (cursor.getTime() <= range.max) {
      ticks.push({
        // eslint-disable-next-line i18next/no-literal-string -- date-fns format pattern, machine input to format() rather than translator-facing copy
        label: format(cursor, 'MMM yyyy'),
        leftPct: ((cursor.getTime() - range.min) / (range.max - range.min)) * 100,
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return ticks;
  }, [range]);

  const todayPct = useMemo(() => {
    if (!range) return null;
    const now = Date.now();
    if (now < range.min || now > range.max) return null;
    return ((now - range.min) / (range.max - range.min)) * 100;
  }, [range]);

  if (!timelineColumn) {
    return (
      <EmptyState
        className="min-h-40"
        title={t('trackersBoard.timelineView.noDateOrDateRange')}
        description={
          canEdit
            ? t('trackersBoard.timelineView.addADateOrDate')
            : t('trackersBoard.viewOnly.noDateColumnDescription')
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="relative h-8 border-b border-border bg-muted/40">
          {monthTicks.map((tick) => (
            <div
              key={tick.label}
              className="absolute top-0 flex h-full items-center border-l border-border pl-1.5 text-[11px] text-muted-foreground"
              style={{ left: `${tick.leftPct}%` }}
            >
              {tick.label}
            </div>
          ))}
        </div>

        {range ? (
          <div className="relative">
            {/* Today marker spans all lanes. */}
            {todayPct != null ? (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="absolute bottom-0 top-0 z-10 w-px bg-destructive/70"
                      style={{ left: `${todayPct}%` }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>{t('trackersBoard.timelineView.today')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
            {lanes.map((lane) => (
              <div
                key={lane.id}
                className="flex min-h-14 items-stretch border-b border-border last:border-b-0"
              >
                <div
                  className="flex w-44 shrink-0 items-center border-r border-border px-3 text-xs font-semibold"
                  style={{ color: lane.color, borderLeft: `3px solid ${lane.color}` }}
                >
                  <span className="truncate">{lane.title}</span>
                </div>
                <div className="relative flex-1 py-2">
                  {lane.dated.map(({ item, start, end }, i) => {
                    const leftPct = ((start.getTime() - range.min) / (range.max - range.min)) * 100;
                    const widthPct =
                      ((end.getTime() - start.getTime()) / (range.max - range.min)) * 100;
                    const top = `${8 + (i % 3) * 24}px`;
                    if (isRange) {
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={onItemClick ? () => onItemClick(item.id) : undefined}
                          title={`${item.title} — ${format(start, 'PP')} → ${format(end, 'PP')}`}
                          className="absolute flex h-5 items-center overflow-hidden rounded-md px-2 text-xs font-medium text-white shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          style={{
                            left: `${leftPct}%`,
                            width: `max(${widthPct}%, 16px)`,
                            top,
                            backgroundColor: lane.color,
                          }}
                        >
                          <span className="truncate">{item.title}</span>
                        </button>
                      );
                    }
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={onItemClick ? () => onItemClick(item.id) : undefined}
                        title={`${item.title} — ${format(start, 'PP')}`}
                        className="absolute flex max-w-56 -translate-x-1 items-center gap-1.5 truncate rounded-full border border-border bg-card py-0.5 pl-1.5 pr-2.5 text-xs shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        style={{ left: `${leftPct}%`, top }}
                      >
                        <span
                          className="size-2 shrink-0 rounded-full"
                          style={{ backgroundColor: lane.color }}
                        />
                        <span className="truncate">{item.title}</span>
                      </button>
                    );
                  })}
                  {/* Reserve height for the tallest stagger slot used. */}
                  <div style={{ height: `${8 + Math.min(lane.dated.length, 3) * 24}px` }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
            {t('trackersBoard.timelineView.noItemsHaveAYet', {
              columnLabel: timelineColumn.label,
            })}
          </div>
        )}
      </div>
    </div>
  );
}
